/**
 * song-syllables.js — syllable + beat-grid analysis for /remove-word-from-song
 *
 * WHY THIS EXISTS (measured 2026-08-02 on a real user file, hs-4@yandex.ru):
 *
 *  1. ASR CANNOT find a duplicated syllable. Whisper is a language model; it
 *     "knows" the line should read "а мы на небо глядим" and collapses the
 *     sung "на на" into one token. The transcript was byte-identical BEFORE
 *     and AFTER the duplicate was removed. So a word list can never surface
 *     this class of bug.
 *  2. The LOUDNESS envelope is blind to legato. Two syllables sung on one
 *     sustained note (A3 throughout, in that file) have no dip between them.
 *     Trusting the envelope made the first attempt cut a PAUSE instead of a
 *     syllable — inaudible edit, user reported "ничего не поменялось".
 *  3. What DOES separate them is articulation: a nasal /n/ or a stop closure
 *     damps the 500-2500 Hz formant region hard, an open vowel fills it. The
 *     ratio of band energy to total energy therefore counts syllables even
 *     when loudness is flat. On the reference file it resolved на|на|не|бо
 *     cleanly where every other method saw one blob.
 *
 * Runs in the browser and in Node (tests/scratch harness) — no DOM, no deps.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.SongSyllables = api;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var ANALYSIS_SR = 22050;   // formant work needs nothing above ~5 kHz
    var NFFT = 1024;
    var BAND_LO = 500;         // F1/F2 region: open vowels live here,
    var BAND_HI = 2500;        // nasals and stops are damped out of it
    var NASAL_DB = -13.0;      // vowelness below this = nasal / closure
    var MIN_EVENT_S = 0.035;   // shorter dips are glottal noise, not articulation

    /* ---------------- FFT (iterative radix-2, in-place) ---------------- */

    function makeTwiddles(n) {
        var cos = new Float32Array(n / 2), sin = new Float32Array(n / 2);
        for (var i = 0; i < n / 2; i++) {
            cos[i] = Math.cos(-2 * Math.PI * i / n);
            sin[i] = Math.sin(-2 * Math.PI * i / n);
        }
        return { cos: cos, sin: sin };
    }

    function fft(re, im, tw) {
        var n = re.length, i, j = 0, k, t;
        for (i = 1; i < n; i++) {                       // bit reversal
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) {
                t = re[i]; re[i] = re[j]; re[j] = t;
                t = im[i]; im[i] = im[j]; im[j] = t;
            }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var step = n / len, half = len >> 1;
            for (i = 0; i < n; i += len) {
                for (k = 0; k < half; k++) {
                    var idx = k * step;
                    var wr = tw.cos[idx], wi = tw.sin[idx];
                    var a = i + k, b = a + half;
                    var xr = re[b] * wr - im[b] * wi;
                    var xi = re[b] * wi + im[b] * wr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi;
                    re[a] += xr;        im[a] += xi;
                }
            }
        }
    }

    function hann(n) {
        var w = new Float32Array(n);
        for (var i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / n);
        return w;
    }

    /* ---------------- resampling (linear; fine below 5 kHz) ------------ */

    function toMono(channels, length) {
        var out = new Float32Array(length), c, i, n = channels.length;
        for (c = 0; c < n; c++) {
            var ch = channels[c];
            for (i = 0; i < length; i++) out[i] += ch[i] / n;
        }
        return out;
    }

    function resample(x, srIn, srOut) {
        if (srIn === srOut) return x;
        var ratio = srIn / srOut;
        var n = Math.floor(x.length / ratio);
        var out = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            var p = i * ratio, i0 = p | 0, f = p - i0;
            var a = x[i0], b = i0 + 1 < x.length ? x[i0 + 1] : a;
            out[i] = a + (b - a) * f;
        }
        return out;
    }

    /* ---------------- the vowelness contour ---------------------------- */

    /**
     * @param {Float32Array} mono  signal at ANALYSIS_SR
     * @param {number} hop         frames step in samples (64 = 2.9 ms)
     * @returns {{t:Float32Array, vowelness:Float32Array, db:Float32Array}}
     */
    function vowelness(mono, hop, refPeak) {
        var tw = makeTwiddles(NFFT), win = hann(NFFT);
        var lo = Math.round(BAND_LO / ANALYSIS_SR * NFFT);
        var hi = Math.round(BAND_HI / ANALYSIS_SR * NFFT);
        var nyq = NFFT >> 1;
        var frames = Math.max(0, Math.floor((mono.length - NFFT) / hop) + 1);
        var t = new Float32Array(frames), v = new Float32Array(frames),
            db = new Float32Array(frames);
        var re = new Float32Array(NFFT), im = new Float32Array(NFFT);
        var peak = 1e-12;

        for (var f = 0; f < frames; f++) {
            var off = f * hop, i;
            for (i = 0; i < NFFT; i++) { re[i] = mono[off + i] * win[i]; im[i] = 0; }
            fft(re, im, tw);
            var band = 0, total = 0;
            for (i = 1; i <= nyq; i++) {
                var p = re[i] * re[i] + im[i] * im[i];
                total += p;
                if (i >= lo && i <= hi) band += p;
            }
            v[f] = 10 * Math.log10(band / (total + 1e-20) + 1e-20);
            db[f] = total;
            if (total > peak) peak = total;
            t[f] = (off + NFFT / 2) / ANALYSIS_SR;
        }
        // A window re-analysed after an edit is measured on the SONG's scale,
        // not its own: its levels must be comparable with the rest of the song.
        if (refPeak > 0) peak = refPeak;
        for (var g = 0; g < frames; g++) db[g] = 10 * Math.log10(db[g] / peak + 1e-20);
        return { t: t, vowelness: v, db: db, hop: hop, peak: peak };
    }

    /** contiguous stretches below NASAL_DB = nasal / stop articulation */
    function articulations(c) {
        var out = [], run = -1;
        for (var i = 0; i < c.vowelness.length; i++) {
            if (c.vowelness[i] < NASAL_DB) { if (run < 0) run = i; }
            else if (run >= 0) {
                if (c.t[i] - c.t[run] > MIN_EVENT_S)
                    out.push({ start: c.t[run], end: c.t[i] });
                run = -1;
            }
        }
        if (run >= 0 && c.t[c.t.length - 1] - c.t[run] > MIN_EVENT_S)
            out.push({ start: c.t[run], end: c.t[c.t.length - 1] });
        return out;
    }

    /* A syllable ENDS where the voice ends, not where the next one begins.
     *
     * Articulations say where a syllable STARTS; they say nothing about where
     * the singing stops. Until 27.09.2026 `end` was simply the next onset, so
     * the last word of a phrase ran through the whole instrumental break and
     * the last word of a song ran to the end of the file. Measured on four
     * real songs: 20–40 % of «syllables» longer than 1 s, one of 39.4 s (a
     * note, 15 s of silence at −40…−53 dB, and a new vowel-initial phrase the
     * detector never split off), and in prod (27.09, #1768) the last syllable
     * of a song was 5.2 s — «stretch the neighbour» then held a 100 ms vowel
     * over all of it (×51) and the user heard «какой-то скрип на этом месте»;
     * the next phrase of the same song was 26 s away, and removing its last
     * word cut the vocal for 26 s.
     *
     * So each articulation span is cut to its VOICED runs: the voice is present
     * while the level is within VOICE_REL_DB of this song's typical sung level
     * (the median syllable peak), a silence of SPLIT_REST_S or more inside a
     * span is a rest (the voice that resumes after it is a new syllable — the
     * vowel-initial case), and HANG_S of decay is kept after the last voiced
     * frame. Legato syllables are untouched: the voice never drops there, so
     * their end is still the next onset. Values read off the level profiles of
     * those four songs: inside a sung note the level stays within ~12 dB of the
     * reference, in a rest it sits 40–53 dB below it. Harness:
     * tests/js/song-syllables.test.mjs (synthetic rests + vowel after silence). */
    var VOICE_REL_DB = 30;
    var SPLIT_REST_S = 0.25;
    var HANG_S = 0.08;
    var MIN_PART_S = 0.10;

    function voicedMask(c, thr) {
        var n = c.t.length, hop = n > 1 ? c.t[1] - c.t[0] : 0.003;
        var k = Math.max(1, Math.round(0.015 / hop)), raw = new Uint8Array(n), on = new Uint8Array(n);
        for (var i = 0; i < n; i++) raw[i] = c.db[i] > thr ? 1 : 0;
        // ±15 ms: a single quiet frame inside a sung note is not a rest
        for (var j = 0; j < n; j++) {
            for (var q = Math.max(0, j - k); q <= Math.min(n - 1, j + k); q++) if (raw[q]) { on[j] = 1; break; }
        }
        return { on: on, hop: hop };
    }

    /** first index with t[i] >= x */
    function lowerBound(t, x) {
        var lo = 0, hi = t.length;
        while (lo < hi) { var mid = (lo + hi) >> 1; if (t[mid] < x) lo = mid + 1; else hi = mid; }
        return lo;
    }

    /**
     * A syllable runs from an articulation onset to where that voiced run ends
     * (see above). We also carry the nucleus (the vowel peak) because that is
     * the material a "stretch" fill loops, and the boundary a splice should
     * land on.
     */
    function syllables(c, floorDb, refDb) {
        floorDb = floorDb === undefined ? -34 : floorDb;
        var arts = articulations(c), spans = [], out = [];
        if (!c.t.length) return out;
        for (var i = 0; i < arts.length; i++) {
            var start = arts[i].start;
            var end = i + 1 < arts.length ? arts[i + 1].start : c.t[c.t.length - 1];
            if (end - start < 0.06) continue;
            var loud = -Infinity;
            for (var f = lowerBound(c.t, start); f < c.t.length && c.t[f] <= end; f++) if (c.db[f] > loud) loud = c.db[f];
            if (loud < floorDb) continue;              // silence, not a syllable
            spans.push({ art: arts[i], start: start, end: end, loud: loud });
        }
        if (!spans.length) return out;
        var louds = spans.map(function (s) { return s.loud; }).sort(function (a, b) { return a - b; });
        var ref = (typeof refDb === 'number' && isFinite(refDb)) ? refDb : louds[louds.length >> 1];
        c.voiceRefDb = ref;
        var thr = ref - VOICE_REL_DB;
        var vm = voicedMask(c, thr), restFrames = Math.round(SPLIT_REST_S / vm.hop);

        spans.forEach(function (sp) {
            var a = lowerBound(c.t, sp.start), b = lowerBound(c.t, sp.end);
            var runs = [], rs = -1, last = -1;
            for (var f = a; f < b; f++) {
                if (!vm.on[f]) continue;
                if (rs < 0) rs = f;
                else if (f - last >= restFrames) { runs.push([rs, last]); rs = f; }
                last = f;
            }
            if (rs >= 0) runs.push([rs, last]);
            runs.forEach(function (r, j) {
                // the articulation onset stays the start unless a rest separates
                // it from the voice (an onset found in silence before a vowel)
                var fromOnset = j === 0 && (r[0] - a) < restFrames;
                var st = fromOnset ? sp.start : c.t[r[0]];
                var en = Math.min(c.t[r[1]] + HANG_S, sp.end);
                if (en - st < 0.06 || (!fromOnset && en - st < MIN_PART_S)) return;
                var best = -Infinity, bestT = (st + en) / 2, lv = -Infinity;
                for (var g = lowerBound(c.t, st); g < c.t.length && c.t[g] <= en; g++) {
                    if (c.vowelness[g] > best) { best = c.vowelness[g]; bestT = c.t[g]; }
                    if (c.db[g] > lv) lv = c.db[g];
                }
                if (lv < floorDb) return;
                var sy = {
                    start: st, end: en,
                    nucleus: bestT,
                    // where the nasal/stop releases; a vowel after a rest has none
                    onsetEnd: fromOnset ? Math.min(Math.max(sp.art.end, st), en) : st,
                    vowelPeak: best, levelDb: lv
                };
                if (en - st > LONG_S) splitLong(c, sy).forEach(function (p) { out.push(p); });
                else out.push(sy);
            });
        });
        return out;
    }

    /* A «syllable» seconds long is a line the absolute rule never cut.
     *
     * NASAL_DB is absolute: a consonant counts only if it pushes the 500–2500 Hz
     * share of the energy below 5 %. In a dense, compressed mix the separated
     * vocal keeps stacked doubles and reverb under every consonant, the share
     * never gets there, and a whole line becomes one block. Prod 01.10.2026
     * (support chat 664): a Suno club track, 111 «syllables» in 3 min, one of
     * them 149.1–174.0 s — «…сердце зе земли, это наша семья, смело твори»;
     * the stray «зе» the user came to remove sat inside it and could not be
     * picked at any zoom. The consonants were still there, only RELATIVE: the
     * level falls 6–13 dB at «зе|зе|мли» against the vowels on either side.
     *
     * So a span longer than LONG_S is re-read for relative dips: a local
     * minimum of the smoothed level (or of the vowelness) that sits DIP_DB
     * (DIP_VOW_DB) below the higher ground on BOTH sides within PROM_S is a
     * consonant; the strongest ones are taken first, no piece shorter than
     * MIN_PIECE_S. A piece starts where its consonant begins (the half-depth
     * point on the falling flank), so picking it takes the consonant with it.
     * A held note with plain vibrato has no such dip and stays whole.
     *
     * Measured on six of our Suno style demos (public/audio/ai-styles, Demucs
     * vocal + Whisper word times): sung time inside «syllables» over 1.2 s
     * 68 % → 13–20 %, word onsets landing on a boundary (±120 ms) 0.39 →
     * 0.62. The price: ~2 pieces per sung vowel — a long note is sometimes cut
     * where the level pumps on the beat. That costs one more click or a drag;
     * an uncut block cannot be picked at all. Higher thresholds (8–10 dB) cut
     * fewer pieces but leave 24–32 % of the singing in blocks again. */
    var LONG_S = 0.9;
    var DIP_DB = 6;
    var DIP_VOW_DB = 4;
    var PROM_S = 0.15;
    var MIN_PIECE_S = 0.12;
    var SMOOTH_S = 0.012;

    function smoothed(x, a, b, k) {
        var out = new Float32Array(b - a + 1);
        for (var f = a; f <= b; f++) {
            var s = 0, n = 0;
            for (var q = Math.max(a, f - k); q <= Math.min(b, f + k); q++) { s += x[q]; n++; }
            out[f - a] = s / n;
        }
        return out;
    }

    function splitLong(c, sy) {
        var a = lowerBound(c.t, sy.start), b = Math.min(c.t.length - 1, lowerBound(c.t, sy.end));
        if (b - a < 8) return [sy];
        var hop = c.t[1] - c.t[0];
        var k = Math.max(1, Math.round(SMOOTH_S / hop)), w = Math.max(2, Math.round(PROM_S / hop));
        var minGap = Math.round(MIN_PIECE_S / hop);
        var d = smoothed(c.db, a, b, k), v = smoothed(c.vowelness, a, b, k), n = d.length;
        var prom = function (x, i) {
            var l = -Infinity, r = -Infinity;
            for (var q = Math.max(0, i - w); q <= i; q++) if (x[q] > l) l = x[q];
            for (var z = i; z <= Math.min(n - 1, i + w); z++) if (x[z] > r) r = x[z];
            return Math.min(l, r) - x[i];
        };
        var cand = [];
        for (var i = minGap; i < n - minGap; i++) {
            var dMin = d[i] <= d[i - 1] && d[i] < d[i + 1];
            var vMin = v[i] <= v[i - 1] && v[i] < v[i + 1];
            if (!dMin && !vMin) continue;
            var score = Math.max(dMin ? prom(d, i) / DIP_DB : 0, vMin ? prom(v, i) / DIP_VOW_DB : 0);
            if (score >= 1) cand.push({ i: i, score: score });
        }
        cand.sort(function (p, q) { return q.score - p.score; });
        var cuts = [];
        cand.forEach(function (cd) {
            for (var j = 0; j < cuts.length; j++) if (Math.abs(cuts[j] - cd.i) < minGap) return;
            cuts.push(cd.i);
        });
        if (!cuts.length) return [sy];
        cuts.sort(function (p, q) { return p - q; });
        // boundary = where the consonant begins: half-depth on the falling flank
        var starts = [], releases = [];
        cuts.forEach(function (ci) {
            var top = d[ci], lo = Math.max(0, ci - w);
            for (var q = ci; q >= lo; q--) if (d[q] > top) top = d[q];
            var half = d[ci] + (top - d[ci]) / 2, s = ci;
            while (s > lo && d[s - 1] < half) s--;
            var hi = Math.min(n - 1, ci + w), e = ci;
            while (e < hi && d[e + 1] < half) e++;
            starts.push(s); releases.push(e);
        });
        var bounds = [sy.start], rels = [sy.onsetEnd];
        starts.forEach(function (s, j) { bounds.push(c.t[a + s]); rels.push(c.t[a + releases[j]]); });
        bounds.push(sy.end);
        var pieces = [];
        for (var p = 0; p + 1 < bounds.length; p++) {
            var st = bounds[p], en = bounds[p + 1], rel = rels[p];
            if (en - st >= 0.06) {
                var best = -Infinity, bestT = (st + en) / 2, lv = -Infinity;
                for (var g = lowerBound(c.t, st); g < c.t.length && c.t[g] <= en; g++) {
                    if (c.vowelness[g] > best) { best = c.vowelness[g]; bestT = c.t[g]; }
                    if (c.db[g] > lv) lv = c.db[g];
                }
                pieces.push({ start: st, end: en, nucleus: bestT,
                              onsetEnd: Math.min(Math.max(rel, st), en),
                              vowelPeak: best, levelDb: lv });
            }
        }
        return pieces.length ? pieces : [sy];
    }

    /* ---------------- beat grid ---------------------------------------- */

    /** spectral-flux onset envelope */
    function onsetEnvelope(mono, hop) {
        var tw = makeTwiddles(NFFT), win = hann(NFFT), nyq = NFFT >> 1;
        var frames = Math.max(0, Math.floor((mono.length - NFFT) / hop) + 1);
        var env = new Float32Array(frames);
        var re = new Float32Array(NFFT), im = new Float32Array(NFFT);
        var prev = new Float32Array(nyq + 1);
        for (var f = 0; f < frames; f++) {
            var off = f * hop, i;
            for (i = 0; i < NFFT; i++) { re[i] = mono[off + i] * win[i]; im[i] = 0; }
            fft(re, im, tw);
            var flux = 0;
            for (i = 1; i <= nyq; i++) {
                var m = Math.sqrt(re[i] * re[i] + im[i] * im[i]);
                var d = m - prev[i];
                if (d > 0) flux += d;
                prev[i] = m;
            }
            env[f] = flux;
        }
        return env;
    }

    /**
     * Tempo by autocorrelation of the onset envelope, phase by correlating a
     * pulse train. Deliberately simple: we only need the SUBDIVISION SPACING
     * to be right — the absolute downbeat is never used, because every edit is
     * expressed as "one slot earlier", not "bar 3 beat 2".
     */
    function beatGrid(mono, srIn, opts) {
        opts = opts || {};
        var mn = resample(mono, srIn, ANALYSIS_SR);
        var hop = 256;
        var env = onsetEnvelope(mn, hop);
        var fps = ANALYSIS_SR / hop;
        var mean = 0, i;
        for (i = 0; i < env.length; i++) mean += env[i];
        mean /= (env.length || 1);
        for (i = 0; i < env.length; i++) env[i] -= mean;

        var minBpm = opts.minBpm || 60, maxBpm = opts.maxBpm || 200;
        var bestLag = 0, bestScore = -Infinity;
        var loLag = Math.floor(60 / maxBpm * fps), hiLag = Math.ceil(60 / minBpm * fps);
        // Log-normal prior around 120 BPM. Without it plain autocorrelation
        // locks onto the half-bar just as happily as the beat — measured: the
        // reference file came out 65.4 BPM instead of 129.2, an exact octave
        // down, which would have made every "one slot" edit twice too long.
        var PRIOR_BPM = opts.priorBpm || 120, PRIOR_W = opts.priorWidth || 0.9;
        for (var lag = loLag; lag <= hiLag && lag < env.length; lag++) {
            var s = 0;
            for (i = 0; i + lag < env.length; i++) s += env[i] * env[i + lag];
            s /= (env.length - lag);
            var oct = Math.log2((60 * fps / lag) / PRIOR_BPM);
            s *= Math.exp(-0.5 * (oct / PRIOR_W) * (oct / PRIOR_W));
            if (s > bestScore) { bestScore = s; bestLag = lag; }
        }
        var beatS = bestLag / fps;
        var bpm = 60 / beatS;

        var bestPhase = 0, bestP = -Infinity;
        for (var p = 0; p < bestLag; p++) {
            var acc = 0, k = 0;
            for (var b = p; b < env.length; b += bestLag) { acc += env[b]; k++; }
            if (k && acc / k > bestP) { bestP = acc / k; bestPhase = p; }
        }
        var beats = [];
        for (var t = bestPhase / fps; t < mn.length / ANALYSIS_SR; t += beatS) beats.push(t);
        var eighth = beatS / 2, sub = [];
        for (var e = bestPhase / fps; e < mn.length / ANALYSIS_SR; e += eighth) sub.push(e);
        return { bpm: bpm, beatS: beatS, eighthS: eighth, beats: beats, subdiv: sub };
    }

    /**
     * Pick the SLOT the editor quantises to.
     *
     * The drums give a beat; the singing decides which subdivision of it the
     * lyric actually walks in. Choosing the slot from the vocal — not from the
     * drums alone — is what makes "pull the phrase back by one slot" land on
     * the grid instead of near it. On the reference file the beat is 464 ms
     * and the line moves in 232 ms eighths; the slot must be the eighth.
     *
     * @returns {{slotS:number, division:number, medianIntervalS:number,
     *            alignmentMs:number}}
     */
    function chooseSlot(grid, syls, opts) {
        opts = opts || {};
        var iv = [];
        for (var i = 1; i < syls.length; i++) iv.push(syls[i].start - syls[i - 1].start);
        if (!iv.length) return { slotS: grid.beatS, division: 1, medianIntervalS: 0, alignmentMs: 0 };
        iv.sort(function (a, b) { return a - b; });
        var med = iv[iv.length >> 1];

        var best = null;
        [1, 2, 3, 4].forEach(function (d) {
            var slot = grid.beatS / d;
            var err = Math.abs(slot - med) / med;
            // how tightly do syllable onsets sit on this slot's phase?
            var dev = 0;
            for (var k = 0; k < syls.length; k++) {
                var r = syls[k].start / slot;
                dev += Math.abs(r - Math.round(r)) * slot;
            }
            dev /= syls.length;
            var score = err + dev / slot;
            if (!best || score < best.score)
                best = { score: score, slotS: slot, division: d, alignmentMs: dev * 1000 };
        });
        return {
            slotS: best.slotS, division: best.division,
            medianIntervalS: med, alignmentMs: best.alignmentMs
        };
    }

    /**
     * Is there actually a voice in this file?
     *
     * Separation ALWAYS returns a "vocal" stem: on a pure instrumental it is
     * bleed and residue, and the syllable detector happily marks that residue
     * up. Measured 2026-08-03: an instrumental with no singing at all produced
     * 42 confident "syllables" and a working edit plan. A user editing a
     * karaoke backing track would get nonsense with no warning at all.
     *
     * The discriminator is level: a real lead vocal is within ~12 dB of the
     * accompaniment, residue sits far below it.
     *
     * @returns {{vocalDb:number, instDb:number, ratioDb:number, hasVocal:boolean}}
     */
    function vocalPresence(vocalMono, instMono, thresholdDb) {
        thresholdDb = thresholdDb === undefined ? -20 : thresholdDb;
        var rms = function (x) {
            var s = 0, n = x.length || 1;
            for (var i = 0; i < x.length; i++) s += x[i] * x[i];
            return Math.sqrt(s / n);
        };
        var v = 20 * Math.log10(rms(vocalMono) + 1e-12);
        var m = 20 * Math.log10(rms(instMono) + 1e-12);
        return { vocalDb: v, instDb: m, ratioDb: v - m, hasVocal: (v - m) > thresholdDb };
    }

    /* ---------------- public entry ------------------------------------- */

    /**
     * @param {Float32Array[]} vocalChannels  separated vocal stem
     * @param {number} sampleRate
     * @param {{hopMs?:number}} [opts]
     */
    function analyzeVocal(vocalChannels, sampleRate, opts) {
        opts = opts || {};
        var len = vocalChannels[0].length;
        var mn = resample(toMono(vocalChannels, len), sampleRate, ANALYSIS_SR);
        var hop = Math.max(32, Math.round((opts.hopMs || 2.9) / 1000 * ANALYSIS_SR));
        var c = vowelness(mn, hop);
        var syl = syllables(c, opts.floorDb);
        return {
            contour: c,
            articulations: articulations(c),
            syllables: syl,
            voiceRefDb: c.voiceRefDb,
            floorDb: opts.floorDb,
            sampleRate: ANALYSIS_SR
        };
    }

    /**
     * After an edit is baked into the vocal, only its neighbourhood changed.
     * Re-analysing the whole song costs 3–4 s on a desktop and up to 10–34 s on
     * a phone (prod `analyze_ms`, 2 weeks to 27.09.2026) — per applied edit,
     * with the page frozen. So the window [fromS, toS] is re-analysed with
     * PAD_S of context on each side, on the song's own scale (its peak and its
     * sung level), and spliced into the existing analysis: syllables that
     * touch the window are replaced, the contour is replaced over the analysed
     * stretch. Returns a NEW analysis object; the old one is not modified.
     */
    function reanalyzeWindow(prev, vocalChannels, sampleRate, fromS, toS, opts) {
        opts = opts || {};
        var PAD_S = 3.0;
        var len = vocalChannels[0].length, dur = len / sampleRate;
        var hop = prev.contour.hop || Math.max(32, Math.round(2.9 / 1000 * ANALYSIS_SR));
        // A syllable that STARTS before the window but runs into it has its end
        // decided by what the edit changed (a legato word before a removed one
        // now ends with its own voice): it is re-read with the window.
        prev.syllables.forEach(function (sy) { if (sy.start < fromS && sy.end > fromS) fromS = sy.start; });
        // start on the song's own frame grid, so the window's frames are the
        // song's frames and a boundary cannot move by a fraction of a hop
        var a = Math.max(0, fromS - PAD_S), b = Math.min(dur, toS + PAD_S);
        var k0 = Math.floor(a * ANALYSIS_SR / hop);
        var i0 = Math.max(0, Math.round(k0 * hop * sampleRate / ANALYSIS_SR));
        var i1 = Math.min(len, Math.ceil(b * sampleRate));
        var parts = vocalChannels.map(function (ch) { return ch.subarray(i0, i1); });
        var mn = resample(toMono(parts, i1 - i0), sampleRate, ANALYSIS_SR);
        var c = vowelness(mn, hop, prev.contour.peak);
        var off = i0 / sampleRate;
        for (var k = 0; k < c.t.length; k++) c.t[k] += off;
        var local = syllables(c, prev.floorDb, prev.voiceRefDb);
        // membership by START: a syllable belongs to the side its onset is on,
        // so none can fall between the old list and the new one
        var before = prev.syllables.filter(function (sy) { return sy.start < fromS; });
        var after = prev.syllables.filter(function (sy) { return sy.start >= toS; });
        var fresh = local.filter(function (sy) { return sy.start >= fromS && sy.start < toS; });
        var all = before.concat(fresh, after);
        for (var z = 0; z + 1 < all.length; z++) {
            if (all[z].end > all[z + 1].start) all[z] = Object.assign({}, all[z], { end: all[z + 1].start });
        }
        // the contour: old frames outside the analysed stretch, new ones inside it
        var pc = prev.contour, T = [], V = [], D = [];
        var cA = c.t.length ? c.t[0] : a, cB = c.t.length ? c.t[c.t.length - 1] : b;
        for (var p = 0; p < pc.t.length && pc.t[p] < cA; p++) { T.push(pc.t[p]); V.push(pc.vowelness[p]); D.push(pc.db[p]); }
        for (var q = 0; q < c.t.length; q++) { T.push(c.t[q]); V.push(c.vowelness[q]); D.push(c.db[q]); }
        for (var r = 0; r < pc.t.length; r++) if (pc.t[r] > cB) { T.push(pc.t[r]); V.push(pc.vowelness[r]); D.push(pc.db[r]); }
        var contour = { t: Float32Array.from(T), vowelness: Float32Array.from(V), db: Float32Array.from(D),
                        hop: pc.hop, peak: pc.peak, voiceRefDb: prev.voiceRefDb };
        return {
            contour: contour,
            articulations: articulations(contour),
            syllables: all,
            voiceRefDb: prev.voiceRefDb,
            floorDb: prev.floorDb,
            sampleRate: ANALYSIS_SR
        };
    }

    return {
        analyzeVocal: analyzeVocal,
        reanalyzeWindow: reanalyzeWindow,
        beatGrid: beatGrid,
        chooseSlot: chooseSlot,
        vocalPresence: vocalPresence,
        vowelness: vowelness,
        articulations: articulations,
        syllables: syllables,
        resample: resample,
        toMono: toMono,
        constants: {
            ANALYSIS_SR: ANALYSIS_SR, NFFT: NFFT,
            BAND_LO: BAND_LO, BAND_HI: BAND_HI, NASAL_DB: NASAL_DB,
            VOICE_REL_DB: VOICE_REL_DB, SPLIT_REST_S: SPLIT_REST_S, HANG_S: HANG_S
        },
        version: '1.2.0'
    };
});
